import React, { useCallback, useRef } from "react";
import {
  Modal,
  ModalBody,
  ModalContent,
  ModalHeader,
  ModalOverlay,
} from "@chakra-ui/modal";
import { useBusinessContext } from "../../context/ModelContext";
import { useApiRequest } from "../../hooks/useApiRequest";
import { PointsForm } from "../../forms/PointsForm";
import { useApiClient } from "../../hooks/useApiClient";
import { LoadingIndicator } from "../LoadingIndicator/LoadingIndicator";
import { ErrorMessage } from "../ErrorMessage/ErrorMessage";
import { useToast } from "../../hooks/useToast";
import { useUserContext } from "../../context/UserContext";
import { ApiLoadingIndicator } from "../ApiLoadingIndicator/ApiLoadingIndicator";

interface TransferPointsModalProps {
  memberId?: string;
  isOpen: boolean;
  onClose: () => void;
}

export const TransferPointsModal = ({
  memberId,
  isOpen,
  onClose,
}: TransferPointsModalProps) => {
  const initialRef = useRef<HTMLInputElement>(null);
  const apiClient = useApiClient();
  const { user } = useUserContext();
  const business = useBusinessContext();
  const { showErrorToast, showSuccessToast } = useToast();

  const apiRequest = useApiRequest(
    (apiClient) => {
      if (!isOpen || !memberId) {
        return null;
      }

      return apiClient.getPointsToCardTransferModalObjects(
        business.BusinessId,
        memberId
      );
    },
    [memberId, business]
  );

  const onSuccess = useCallback(
    (message: string) => {
      showSuccessToast(message);
      onClose();
    },
    [onClose, showSuccessToast]
  );

  return (
    <Modal initialFocusRef={initialRef} isOpen={isOpen} onClose={onClose}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Transfer Points To CherryPay Card</ModalHeader>
        <ModalBody pb="6">
          <ApiLoadingIndicator
            renderLoading={() => <LoadingIndicator color="#333" />}
            renderError={() => (
              <ErrorMessage>
                {"An error was encountered while loading the form."}
              </ErrorMessage>
            )}
            apiStates={[apiRequest]}
          >
            {([{ Member, PointsTypes, CherryPayCards, Minimum, Maximum }]) => (
              <PointsForm
                username={user?.email ?? ""}
                apiClient={apiClient}
                businessId={business.BusinessId}
                member={Member}
                formType="transfer-points-to-cpc"
                cards={CherryPayCards}
                minimum={Minimum}
                maximum={Maximum}
                pointsTypes={PointsTypes}
                onCancel={onClose}
                onSuccess={onSuccess}
                onFailure={showErrorToast}
              />
            )}
          </ApiLoadingIndicator>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};
